


import React, { useMemo, useState } from 'react';
import CustomSelect from './CustomSelect';
import { useTranslation } from '../../hooks/useTranslation';
import { createStep1Schema } from './validation/step1Schema';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// Build translated city options
const getCityOptions = (t) => [
  { value: 'riyadh', label: t('cities.riyadh') },
  { value: 'jeddah', label: t('cities.jeddah') },
  { value: 'makkah', label: t('cities.makkah') },
  { value: 'madinah', label: t('cities.madinah') },
  { value: 'eastern-province', label: t('cities.eastern-province') },
  { value: 'qassim', label: t('cities.qassim') },
  { value: 'asir', label: t('cities.asir') },
  { value: 'tabuk', label: t('cities.tabuk') },
  { value: 'hail', label: t('cities.hail') },
  { value: 'northern-borders', label: t('cities.northern-borders') },
  { value: 'jazan', label: t('cities.jazan') },
  { value: 'najran', label: t('cities.najran') },
  { value: 'albaha', label: t('cities.albaha') },
  { value: 'aljawf', label: t('cities.aljawf') },
  { value: 'khobar', label: t('cities.khobar') },
  { value: 'dammam', label: t('cities.dammam') },
  { value: 'dhahran', label: t('cities.dhahran') },
  { value: 'al-ahsa', label: t('cities.al-ahsa') },
  { value: 'jubail', label: t('cities.jubail') },
  { value: 'qatif', label: t('cities.qatif') },
  { value: 'ras-tanura', label: t('cities.ras-tanura') },
  { value: 'khafji', label: t('cities.khafji') },
  { value: 'nuairiyah', label: t('cities.nuairiyah') },
  { value: 'buqayq', label: t('cities.buqayq') },
];

export default function Step1({ formData, setFormData }) {
  const { t } = useTranslation();
  const cityOptions = useMemo(() => getCityOptions(t), [t]);
  const [errors, setErrors] = useState({});
  const selectedCountry = 'sa';
  const schema = useMemo(() => createStep1Schema(t, selectedCountry), [t, selectedCountry]);

  const setField = (key) => (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, [key]: val }));

    if (key === 'fullName') validateField('fullName', {}, { fullName: val });
    if (key === 'email') validateField('email', {}, { email: val });

    if (key === 'phone') {
      const digits = String(val).replace(/\D/g, '');
      const sanitized = digits ? (digits.startsWith('966') ? `+${digits}` : `+966${digits}`) : '';
      validateField('phone', { phone: true }, { phone: sanitized });
    }
  };

  const toggleGender = (val) => {
    const nextVal = formData.gender === val ? '' : val;
    setFormData((prev) => ({ ...prev, gender: nextVal }));
    validateField('gender', {}, { gender: nextVal });
  };

  const validateField = async (field, touchedCtx = {}, overrides = {}) => {
    const values = {
      ...formData,
      ...overrides,
    };

    try {
      await schema.validateAt(field, values, { context: { touched: touchedCtx } });
      setErrors((prev) => ({ ...prev, [field]: '' }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [field]: err.message }));
    }
  };

  const handlePhoneBlur = () => {
    validateField('phone', { phone: true });
  };

  const formatYMD = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return (
    <div className="space-y-5">
      <p className="text-[20px] my-5 font-[700] text-[#5D4A9D] text-start">
        {t('registration.step1Title')}
      </p>

      <div className="mb-4 text-start">
        <label className="text-[14px] font-[500] mb-2">
          {t('registration.fullName.label')} <span>*</span>
        </label>
        <input
          type="text"
          value={formData.fullName || ''}
          onChange={setField('fullName')}
          placeholder={t('registration.fullName.placeholder')}
          className="w-full border-[1px] rounded-[8px] px-[18px] text-[16px] py-2 mt-2 bg-[#F5F5F5] border-[#E6E6E6] h-[50px] placeholder:text-[16px] focus:bg-[#fff] focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D]"
        />
        {errors.fullName ? (
          <p className="text-red-500 text-[12px] mt-1 text-start">{errors.fullName}</p>
        ) : null}
      </div>

      <div className="mb-4 text-start w-full">
        <label className="text-[14px] font-[500] mb-2">
          {t('registration.birthDate.label')} <span>*</span>
        </label>
        <div className="relative w-full mt-2">
          <DatePicker
            selected={formData.birthDate ? new Date(formData.birthDate) : null}
            onChange={(date) => {
              const formatted = formatYMD(date);
              setFormData((prev) => ({ ...prev, birthDate: formatted }));
              validateField('birthDate', {}, { birthDate: formatted });
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText={t('registration.birthDate.placeholder') || 'DD/MM/YYYY'}
            className={`!w-full border-[1px] rounded-[8px] px-[18px] py-2 h-[50px] text-[14px] focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D] border-[#E6E6E6] ${formData.birthDate ? 'bg-white' : 'bg-[#F5F5F5] focus:bg-white'
              }`}
            showPopperArrow={false}
            maxDate={new Date()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
          />
          <div className="absolute top-0 h-full flex items-center rtl:right-auto rtl:left-[20px] right-[20px]">
            <img src="/cal3.svg" alt="" />
          </div>
        </div>
        {errors.birthDate ? (
          <p className="text-red-500 text-[12px] mt-1 text-start">{errors.birthDate}</p>
        ) : null}
      </div>

      <div className="mb-2 text-start">
        <label className="text-[14px] font-[500] mb-2 !text-start">
          {t('registration.gender.label')} <span>*</span>
        </label>
        <div className="flex flex-col gap-4 mt-2">
          {['male', 'female'].map((g) => (
            <label key={g} className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.gender === g}
                onChange={() => toggleGender(g)}
                className="hidden"
              />
              <span
                className={`w-[18px] h-[18px] rounded-[4px] border-[1px] border-[#E6E6E6] flex items-center justify-center shadow-[0px_1px_2px_0px_#0000000D] ${formData.gender === g ? 'bg-[#5D4A9D] text-white' : 'bg-[#F5F5F5]'
                  }`}
              />
              <span className="text-[#151515] text-[16px] font-[400] capitalize">
                {g === 'male' ? t('registration.gender.male') : t('registration.gender.female')}
              </span>
            </label>
          ))}
        </div>

        {errors.gender ? (
          <p className="text-red-500 text-[12px] mt-1 text-start">{errors.gender}</p>
        ) : null}
      </div>

      <div className="mb-4 text-start">
        <label className="text-[14px] font-[500] mb-2">
          {t('registration.city.label')} <span>*</span>
        </label>
        <CustomSelect
          options={cityOptions}
          value={formData.city || ''}
          onChange={(v) => {
            setFormData((prev) => ({ ...prev, city: v }));
            validateField('city', {}, { city: v });
          }}
          onBlur={() => validateField('city', {}, { city: formData.city })}
          placeholder={t('registration.city.placeholder')}
        />
        {errors.city ? (
          <p className="text-red-500 text-[12px] mt-1 text-start">{errors.city}</p>
        ) : null}
      </div>

      <div className="mb-4 text-start">
        <label className="text-[14px] font-[500] mb-2">
          {t('registration.phone.label')} *
        </label>
        <div className="flex ltr rtl:ltr border-[1px] rounded-[8px] px-[18px] mt-2 bg-[#F5F5F5] border-[#E6E6E6] py-0 focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D]">
          <div className="border-r-[1px] gap-1 border-[#E0DEF7] flex items-center w-[80px] font-[500] text-[14px]">
            <img src="/ksaflag.svg" alt="" />
            +966
          </div>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={setField('phone')}
            onBlur={handlePhoneBlur}
            placeholder={t('registration.phone.placeholder')}
            className="w-full h-[48px] placeholder:text-[16px] px-2 bg-[#F5F5F5] focus:outline-none focus:ring-[0]"
          />
        </div>
        {errors.phone ? (
          <p className="text-red-500 text-[12px] mt-1 text-start">{errors.phone}</p>
        ) : null}
      </div>

      <div className="mb-4 text-start">
        <label className="text-[14px] font-[500] mb-2">
          {t('registration.email.label')} <span>*</span>
        </label>
        <input
          type="email"
          value={formData.email || ''}
          onChange={setField('email')}
          onBlur={() => validateField('email')}
          placeholder={t('registration.email.placeholder')}
          className="w-full border-[1px] rounded-[8px] px-[18px] text-[16px] py-2 mt-2 bg-[#F5F5F5] border-[#E6E6E6] h-[50px] placeholder:text-[16px] focus:bg-[#fff] focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D]"
        />
        {errors.email ? (
          <p className="text-red-500 text-[12px] mt-1 text-start">{errors.email}</p>
        ) : null}
      </div>
    </div>
  );
}